// frontend/src/utils/blockRegistry.ts
import { BlockRegistryEntry, BlockType } from '../types/Portfolio';

export const BLOCK_REGISTRY: BlockRegistryEntry[] = [
  // ============================================================================
  // FOUNDATION BLOCKS
  // ============================================================================
  {
    type: 'hero',
    label: 'Hero Section',
    icon: 'ImageIcon',
    description: 'Large banner with title, subtitle, and background image',
    category: 'foundation',
    defaultData: {
      title: '',
      subtitle: '',
      backgroundImage: '',
      overlayOpacity: 0.4,
      ctaText: '',
      ctaLink: ''
    },
    editFields: [
      {
        name: 'title',
        type: 'text',
        label: 'Title',
        required: true,
        helpText: 'Main headline for the hero section'
      },
      {
        name: 'subtitle',
        type: 'text',
        label: 'Subtitle',
        required: false,
        helpText: 'Supporting text below the title'
      },
      {
        name: 'backgroundImage',
        type: 'image-picker',
        label: 'Background Image',
        required: false,
        helpText: 'Full-width background image'
      },
      {
        name: 'overlayOpacity',
        type: 'slider',
        label: 'Overlay Opacity',
        required: false,
        min: 0,
        max: 1,
        step: 0.1,
        helpText: 'Darkness of overlay on background image (0 = transparent, 1 = black)'
      },
      {
        name: 'ctaText',
        type: 'text',
        label: 'Call to Action Text',
        required: false,
        helpText: 'Button text (optional)'
      },
      {
        name: 'ctaLink',
        type: 'text',
        label: 'Call to Action Link',
        required: false,
        helpText: 'URL or anchor (#offerings) for the button'
      }
    ]
  },

{
  type: 'image_highlight',
  label: 'Image Highlight',
  icon: 'ImageIcon',
  description: 'Full-screen impactful image with overlay text - perfect for stats, quotes, or key messages',
  category: 'foundation',
  defaultData: {
    backgroundImage: '',
    primary: '',
    secondary: '',
    position: 'bottom',
    overlayOpacity: 0.5,
    textAlign: 'center',
    primaryTextColor: 'white',
    secondaryTextColor: 'white',
    primaryFontSize: 'large',      // ✅ ADD THIS
    secondaryFontSize: 'medium'    // ✅ ADD THIS
  },
  editFields: [
    {
      name: 'backgroundImage',
      type: 'image-picker',
      label: 'Background Image',
      required: true,
      helpText: 'Full-screen background image'
    },
    {
      name: 'primary',
      type: 'text',
      label: 'Primary Text',
      required: true,
      helpText: 'Main text - could be a stat like "2,400" or a statement like "Ireland\'s Premier Venue"'
    },
    {
      name: 'primaryFontSize',
      type: 'select',
      label: 'Primary Text Size',
      required: false,
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large- Default' },
        { value: 'xlarge', label: 'Extra Large' }
      ],
      helpText: 'Adjust size based on text length - use smaller for long text, larger for short/impact words'
    },
    {
      name: 'secondary',
      type: 'text',
      label: 'Secondary Text',
      required: false,
      helpText: 'Supporting text below primary - e.g., "Monthly Guests"'
    },
    {
      name: 'secondaryFontSize',
      type: 'select',
      label: 'Secondary Text Size',
      required: false,
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium- Default' },
        { value: 'large', label: 'Large' }
      ],
      helpText: 'Adjust size based on text length'
    },
    {
      name: 'primaryTextColor',
      type: 'select',
      label: 'Primary Text Color',
      required: false,
      options: [
        { value: 'white', label: 'White' },
        { value: 'primary', label: 'Theme Primary Color' },
        { value: 'accent', label: 'Theme Accent Color' }
      ],
      helpText: 'Choose text color based on background image'
    },
    {
      name: 'secondaryTextColor',
      type: 'select',
      label: 'Secondary Text Color',
      required: false,
      options: [
        { value: 'white', label: 'White' },
        { value: 'primary', label: 'Theme Primary Color' },
        { value: 'accent', label: 'Theme Accent Color' }
      ],
      helpText: 'Choose text color based on background image'
    },
    {
      name: 'position',
      type: 'select',
      label: 'Text Position',
      required: true,
      options: [
        { value: 'top', label: 'Top Third' },
        { value: 'center', label: 'Center' },
        { value: 'bottom', label: 'Bottom Third' }
      ]
    },
    {
      name: 'textAlign',
      type: 'select',
      label: 'Text Alignment',
      required: true,
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' }
      ]
    },
    {
      name: 'overlayOpacity',
      type: 'slider',
      label: 'Overlay Darkness',
      required: false,
      min: 0,
      max: 1,
      step: 0.1,
      helpText: 'Darkens image so text is readable (0 = transparent, 1 = black)'
    }
  ]
},
  // ============================================================================
// AUDIENCE DEMOGRAPHICS BLOCK - REGISTRY ENTRY
// ============================================================================
// FILE: frontend/src/utils/blockRegistry.tsx
// ACTION: Add to BLOCK_REGISTRY array

{
  type: 'audience_demographics',
  label: 'Audience Demographics',
  icon: 'PeopleIcon',
  description: 'Display audience age, professional profile, spending, geographic reach, and online engagement data',
  category: 'impact',
  defaultData: {
    ageDistribution: {
      data: [
        { ageRange: '25-34', percentage: 28 },
        { ageRange: '35-44', percentage: 35 },
        { ageRange: '45-54', percentage: 22 },
        { ageRange: '55+', percentage: 15 }
      ],
      showPieChart: true
    },
    professionalProfile: {
      data: [
        { category: 'Tech & Creative Industries', percentage: 42 },
        { category: 'Finance & Legal', percentage: 18 },
        { category: 'Education & Arts', percentage: 25 },
        { category: 'Other Professionals', percentage: 15 }
      ],
      showPieChart: true
    },
    averageSpend: 65,                    // ADD THIS LINE
    averageSpendBgColor: '',             // ADD THIS LINE (empty = use theme color)
    geographicReach: {
      data: [
        { location: 'Dublin 4/6/8', percentage: 58 },
        { location: 'Greater Dublin', percentage: 28 },
        { location: 'International Visitors', percentage: 14 }
      ],
      showPieChart: false
    },
    onlineEngagement: {
      livestreamAvgViewers: 2400,
      youtubeSubscribers: 8500,
      monthlyWatchHours: 12000,
      platformImpressions: 85000
    },
    enableAnimations: true
  },
  editFields: [
    {
      name: 'enableAnimations',
      type: 'toggle',
      label: 'Enable Number Animations',
      helpText: 'Numbers will count up when block enters viewport'
    },
    {
      name: 'ageDistribution',
      type: 'text',
      label: 'Age Distribution',
      helpText: 'Add age ranges and percentages (must total 100%)'
    },
    {
      name: 'ageDistribution.showPieChart',
      type: 'toggle',
      label: 'Show Age Distribution as Pie Chart'
    },
    {
      name: 'professionalProfile',
      type: 'text',
      label: 'Professional Profile',
      helpText: 'Add professional categories and percentages (must total 100%)'
    },
    {
      name: 'professionalProfile.showPieChart',
      type: 'toggle',
      label: 'Show Professional Profile as Pie Chart'
    },
    {
      name: 'averageSpend',
      type: 'number',
      label: 'Average Spend Per Visit (€)',
      required: true,
      min: 0,
      helpText: 'Average amount spent per visitor (tickets + bar + merchandise)'
    },
    {
      name: 'averageSpendBgColor',       // ADD THIS ENTIRE OBJECT
      type: 'color',
      label: 'Average Spend Background Color',
      required: false,
      helpText: 'Choose background color for the Average Spend card (leave empty to use theme primary color)'
    },
    {
      name: 'geographicReach',
      type: 'text',
      label: 'Geographic Reach',
      helpText: 'Add locations and percentages (must total 100%)'
    },
    {
      name: 'geographicReach.showPieChart',
      type: 'toggle',
      label: 'Show Geographic Reach as Pie Chart'
    },
    {
      name: 'onlineEngagement.livestreamAvgViewers',
      type: 'number',
      label: 'Livestream Average Viewers',
      min: 0,
      helpText: 'Average number of viewers per livestream'
    },
    {
      name: 'onlineEngagement.youtubeSubscribers',
      type: 'number',
      label: 'YouTube Subscribers',
      min: 0
    },
    {
      name: 'onlineEngagement.monthlyWatchHours',
      type: 'number',
      label: 'Monthly Watch Hours',
      min: 0,
      helpText: 'Total hours of content watched per month'
    },
    {
      name: 'onlineEngagement.platformImpressions',
      type: 'number',
      label: 'Platform Impressions',
      min: 0,
      helpText: 'Total monthly reach across all platforms'
    }
  ]
},

{
  type: 'programme_initiatives',
  label: 'Our Programs & Initiatives',
  icon: 'AccountTreeIcon',
  description: 'Showcase organizational programs, initiatives, and projects with timeline or card layouts',
  category: 'programs',
  defaultData: {
    sectionTitle: 'Our Programs & Initiatives',
    sectionSubtitle: 'Beyond our venue, we deliver nationwide artist development, commissioning programs, and bespoke cultural programming.',
    titleColor: 'primary',
    subtitleColor: 'primary',
    programs: [],
    layout: 'cards',
    showStatusBadges: true,
    showFundingBadges: true
  },
  editFields: [
    {
      name: 'sectionTitle',
      type: 'text',
      label: 'Section Title',
      required: true,
      helpText: 'Main heading for this section'
    },
    {
      name: 'sectionSubtitle',
      type: 'rich-text',
      label: 'Section Subtitle',
      required: false,
      helpText: 'Contextual description below the title'
    },
    {
      name: 'titleColor',
      type: 'select',
      label: 'Title Color',
      required: true,
      options: [
        { value: 'primary', label: 'Theme Primary Color' },
        { value: 'accent', label: 'Theme Accent Color' }
      ]
    },
    {
      name: 'subtitleColor',
      type: 'select',
      label: 'Subtitle Color',
      required: true,
      options: [
        { value: 'primary', label: 'Theme Primary Color' },
        { value: 'accent', label: 'Theme Accent Color' }
      ]
    },
    {
      name: 'programs',
      type: 'text',
      label: 'Programs & Initiatives',
      required: true,
      helpText: 'Add programs, initiatives, and projects'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout Style',
      required: true,
      options: [
        { value: 'cards', label: 'Cards - Grid layout' },
        { value: 'timeline', label: 'Timeline - Chronological view' },
        { value: 'list', label: 'List - Detailed stacked view' }
      ]
    },
    {
      name: 'showStatusBadges',
      type: 'toggle',
      label: 'Show Status Badges',
      helpText: 'Display Active/Past/Seasonal/On-Demand badges on programs'
    },
    {
      name: 'showFundingBadges',
      type: 'toggle',
      label: 'Show Funding Available Badges',
      helpText: 'Display "Funding Available" badge on applicable programs'
    }
  ]
},

  {
    type: 'mission_vision',
    label: 'Mission & Vision',
    icon: 'FlagIcon',
    description: 'Organizational purpose, values, and artistic vision',
    category: 'foundation',
    defaultData: {
      mission: '',
      vision: '',
      values: [],
      artisticVision: '',
      communityCommitment: '',
      layout: 'stacked'
    },
    editFields: [
      {
        name: 'mission',
        type: 'textarea',
        label: 'Mission Statement',
        required: true,
        helpText: 'Your organizational mission'
      },
      {
        name: 'vision',
        type: 'textarea',
        label: 'Vision Statement',
        required: true,
        helpText: 'Your long-term vision'
      },
      {
        name: 'values',
        type: 'textarea',
        label: 'Core Values',
        required: false,
        helpText: 'One value per line'
      },
      {
        name: 'artisticVision',
        type: 'textarea',
        label: 'Artistic Vision',
        required: false,
        helpText: 'Your artistic direction and philosophy'
      },
      {
        name: 'communityCommitment',
        type: 'textarea',
        label: 'Community Commitment',
        required: false,
        helpText: 'How you serve your community'
      },
      {
        name: 'layout',
        type: 'select',
        label: 'Layout',
        required: true,
        options: [
          { value: 'stacked', label: 'Stacked (Vertical)' },
          { value: 'side-by-side', label: 'Side by Side' }
        ]
      }
    ]
  },

  // ============================================================================
  // IMPACT BLOCKS
  // ============================================================================
 {
  type: 'metrics',
  label: 'Metrics',
  icon: 'BarChartIcon',
  description: 'Quantitative impact statistics',
  category: 'impact',
  defaultData: {
    metrics: [],
    layout: 'grid',
    enableAnimations: true  // ADD THIS
  },
  editFields: [
    {
      name: 'metrics',
      type: 'textarea',  // This stays as is - it's handled by BlockFormBuilder's array logic
      label: 'Metrics',
      required: true,
      helpText: 'Enter metrics'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'grid', label: 'Grid' },
        { value: 'row', label: 'Row' }
      ]
    },
    // ADD THIS NEW FIELD
    {
      name: 'enableAnimations',
      type: 'toggle',
      label: 'Enable Scroll Animations',
      helpText: 'Animate numbers when block comes into view'
    }
  ]
},

  {
  type: 'media_reach',
  label: 'Media Reach',
  icon: 'CampaignIcon',
  description: 'Display social media reach, press coverage, email stats, and estimated media value with optional background image',
  category: 'impact',
  defaultData: {
    backgroundImage: '',
    totalMonthlyReach: 85000,
    socialMedia: [
      { platform: 'Instagram', followerCount: 12400 },
      { platform: 'Facebook', followerCount: 8900 }
    ],
    pressCoverage: [
      { publication: 'Irish Times', mentionCount: 3 },
      { publication: 'RTÉ Culture', mentionCount: 5 }
    ],
    emailStats: {
      subscriberCount: 6200,
      openRate: 34
    },
    websiteTraffic: 18000,
    estimatedMediaValue: 45000,
    enableAnimations: true
  },
  editFields: [
    {
      name: 'backgroundImage',
      type: 'image-picker',
      label: 'Background Image (Optional)',
      helpText: 'Background image with dark overlay for visual interest'
    },
    {
      name: 'enableAnimations',
      type: 'toggle',
      label: 'Enable Number Animations',
      helpText: 'Numbers will count up when block enters viewport'
    },
    {
      name: 'totalMonthlyReach',
      type: 'number',
      label: 'Total Monthly Reach',
      required: true,
      min: 0,
      helpText: 'Total monthly impressions across all platforms'
    },
    {
      name: 'socialMedia',
      type: 'text',
      label: 'Social Media Platforms',
      helpText: 'Add social media platforms and follower counts'
    },
    {
      name: 'pressCoverage',
      type: 'text',
      label: 'Press Coverage',
      helpText: 'Add publications and mention counts'
    },
    {
      name: 'emailStats.subscriberCount',
      type: 'number',
      label: 'Email Subscribers',
      min: 0,
      helpText: 'Total email list size'
    },
    {
      name: 'emailStats.openRate',
      type: 'number',
      label: 'Email Open Rate (%)',
      min: 0,
      max: 100,
      helpText: 'Average email open rate percentage'
    },
    {
      name: 'websiteTraffic',
      type: 'number',
      label: 'Monthly Website Visitors',
      min: 0,
      helpText: 'Average monthly website traffic'
    },
    {
      name: 'estimatedMediaValue',
      type: 'number',
      label: 'Estimated Media Value (€)',
      min: 0,
      helpText: 'Total estimated PR value'
    }
  ]
},

{
  type: 'brand_visibility',
  label: 'Brand Visibility',
  icon: 'VisibilityIcon',
  description: 'Showcase where sponsor branding appears across digital, print, venue, and press with image carousels and metrics',
  category: 'impact',
  defaultData: {
    categories: [
      {
        name: 'digital',
        label: 'Digital',
        images: [
          { url: '', caption: 'Homepage banner (18K monthly views)' },
          { url: '', caption: 'Email campaigns (6,200 subscribers)' },
          { url: '', caption: 'Social media posts (12 per season)' },
          { url: '', caption: 'Livestream overlays (avg 2,400 viewers)' }
        ],
        metricCallout: '18K monthly website visitors + 85K social impressions',
        description: '<p><strong>Your brand appears:</strong></p><ul><li>Homepage banner rotation</li><li>Event page headers</li><li>Email campaign headers (6,200 subscribers, 34% open rate)</li><li>Social media posts throughout the season</li><li>Livestream overlays during performances</li></ul>',
        anchorLinkText: 'See our full media reach',
        anchorLinkBlockId: ''
      },
      {
        name: 'print',
        label: 'Print',
        images: [
          { url: '', caption: 'Concert posters (200 per show)' },
          { url: '', caption: 'Programme booklets' },
          { url: '', caption: 'Bar menus and table tents' }
        ],
        metricCallout: '200 posters distributed per show + handed to all attendees',
        description: '<p><strong>Your brand appears:</strong></p><ul><li>Concert posters (200 distributed per show across Dublin venues)</li><li>Programme booklets (handed to every attendee)</li><li>Bar menus and table tents (visible throughout venue)</li><li>Promotional flyers</li></ul>',
        anchorLinkText: '',
        anchorLinkBlockId: ''
      },
      {
        name: 'venue',
        label: 'Venue',
        images: [
          { url: '', caption: 'Entrance banner (2m x 1m)' },
          { url: '', caption: 'Stage backdrop during performances' },
          { url: '', caption: 'Bar area signage' },
          { url: '', caption: 'Digital screens (2 locations)' }
        ],
        metricCallout: '8,500+ attendees see your branding at every show',
        description: '<p><strong>Your brand appears:</strong></p><ul><li>Entrance banner (2m x 1m, visible to all arrivals)</li><li>Stage backdrop throughout performances</li><li>Bar area signage (high dwell time)</li><li>Digital screens in 2 venue locations</li><li>Foyer displays</li></ul>',
        anchorLinkText: 'See our audience demographics',
        anchorLinkBlockId: ''
      },
      {
        name: 'press',
        label: 'Press',
        images: [
          { url: '', caption: 'Press release template' },
          { url: '', caption: 'Media kit cover' },
          { url: '', caption: 'Press announcement example' }
        ],
        metricCallout: '€45K estimated media value from press coverage',
        description: '<p><strong>Your brand appears:</strong></p><ul><li>Named in all press releases</li><li>Quoted in sponsor announcements</li><li>Logo in media kits distributed to journalists</li><li>Credited in all press coverage</li><li>Featured in annual reports</li></ul>',
        anchorLinkText: 'See our press coverage',
        anchorLinkBlockId: ''
      }
    ],
    logoSpecs: 'Logo Requirements: Minimum 200px width for digital assets, 10cm width for print materials. Vector format (SVG, EPS, or AI) preferred for scalability.'
  },
  editFields: [
    {
      name: 'categories',
      type: 'text',
      label: 'Brand Visibility Categories',
      helpText: 'Configure Digital, Print, Venue, and Press visibility sections'
    },
    {
      name: 'logoSpecs',
      type: 'textarea',
      label: 'Logo Specifications',
      helpText: 'Technical requirements for sponsor logos (sizes, formats, etc.)'
    }
  ]
},

{
  type: 'video_showcase',
  label: 'Video Showcase',
  icon: 'PlayCircleIcon',
  description: 'Display YouTube/Vimeo videos in grid, featured, or carousel layouts with modal popups',
  category: 'media',
  defaultData: {
    heading: 'See The Cooler in Action',
    description: 'Watch highlights from our performances, artist testimonials, and behind-the-scenes moments',
    layout: 'grid',
    videos: [
      {
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: '2024 Season Highlights',
        description: 'A compilation of our best performances from the 2024 season',
        metricCallout: '12K views',
        category: 'Performance',
        ctaText: '',
        ctaLink: ''
      },
      {
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: 'Artist Testimonial: Max Zaska',
        description: 'Hear from Irish artist Max Zaska about his experience at The Cooler',
        metricCallout: 'Featured Artist',
        category: 'Testimonial',
        ctaText: '',
        ctaLink: ''
      },
      {
        url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
        title: 'Behind the Scenes: Venue Tour',
        description: 'Take a virtual tour of our rehearsal and performance spaces',
        metricCallout: '5.2K views',
        category: 'Behind the Scenes',
        ctaText: '',
        ctaLink: ''
      }
    ],
    showModal: true,
    autoplayFeatured: false,
    sectionCtaText: 'View Our Full Channel',
    sectionCtaLink: 'https://www.youtube.com/@yourchannelname'
  },
  editFields: [
    {
      name: 'heading',
      type: 'text',
      label: 'Section Heading',
      required: true,
      helpText: 'Main title for the video showcase section'
    },
    {
      name: 'description',
      type: 'textarea',
      label: 'Section Description',
      helpText: 'Optional introductory text below the heading'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout Style',
      required: true,
      options: [
        { value: 'grid', label: 'Grid - Multiple videos in cards' },
        { value: 'featured', label: 'Featured - One large + grid below' },
        { value: 'carousel', label: 'Carousel - Swipeable slides' }
      ],
      helpText: 'Choose how videos are displayed'
    },
    {
      name: 'videos',
      type: 'text',
      label: 'Videos',
      helpText: 'Add YouTube or Vimeo videos with titles, descriptions, and metadata'
    },
    {
      name: 'showModal',
      type: 'toggle',
      label: 'Open Videos in Modal Popup',
      helpText: 'Videos open full-screen when clicked (recommended)'
    },
    {
      name: 'autoplayFeatured',
      type: 'toggle',
      label: 'Autoplay Featured Video (Muted)',
      helpText: 'Only applies to Featured layout - first video autoplays on mute'
    },
    {
      name: 'sectionCtaText',
      type: 'text',
      label: 'Section CTA Button Text (Optional)',
      helpText: 'e.g., "View Our Full YouTube Channel"'
    },
    {
      name: 'sectionCtaLink',
      type: 'text',
      label: 'Section CTA Button Link (Optional)',
      helpText: 'URL for the section-level call-to-action button'
    }
  ]
},

{
  type: 'links',
  label: 'Links & Connect',
  icon: 'LinkIcon',
  description: 'Display social media links and important resource links in a professional hub',
  category: 'engagement',
  defaultData: {
    heading: 'Connect With Us',
    description: 'Follow our journey and access key resources',
    layout: 'hybrid',
    
    socialLinks: [
      {
        platform: 'instagram',
        url: 'https://instagram.com/yourcooler',
        handle: '@yourcooler',
        followerCount: '12.5K followers'
      },
      {
        platform: 'facebook',
        url: 'https://facebook.com/thecooler',
        handle: 'The Cooler',
        followerCount: '8.2K likes'
      },
      {
        platform: 'youtube',
        url: 'https://youtube.com/@thecooler',
        handle: '@thecooler',
        followerCount: '5.4K subscribers'
      },
      {
        platform: 'twitter',
        url: 'https://twitter.com/thecooler',
        handle: '@thecooler',
        followerCount: '3.1K followers'
      }
    ],
    
    resourceLinks: [
      {
        title: 'Book Tickets',
        description: 'Reserve your seat for our next performance',
        url: 'https://ticketmaster.com/thecooler',
        icon: 'ticket',
        ctaText: 'Book Now',
        openInNewTab: true,
        backgroundColor: ''
      },
      {
        title: 'Partnership Prospectus',
        description: 'Download our partnership opportunities PDF',
        url: '/downloads/partnership-prospectus.pdf',
        icon: 'partnership',
        ctaText: 'Download PDF',
        openInNewTab: true,
        backgroundColor: ''
      },
      {
        title: 'Media Kit',
        description: 'Logos, photos, and press materials',
        url: '/downloads/media-kit.zip',
        icon: 'press',
        ctaText: 'Download',
        openInNewTab: false,
        backgroundColor: ''
      },
      {
        title: 'Subscribe to Newsletter',
        description: 'Get monthly updates on performances and artist news',
        url: 'https://mailchimp.com/signup',
        icon: 'email',
        ctaText: 'Subscribe',
        openInNewTab: true,
        backgroundColor: ''
      }
    ],
    
    showContactCta: true,
    contactCtaHeading: 'Ready to Partner?',
    contactCtaDescription: 'Let\'s discuss how we can work together to support Irish jazz',
    contactCtaButtonText: 'Get in Touch',
    contactCtaButtonLink: '/contact'
  },
  editFields: [
    {
      name: 'heading',
      type: 'text',
      label: 'Section Heading',
      required: true
    },
    {
      name: 'description',
      type: 'textarea',
      label: 'Section Description'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout Style',
      required: true,
      options: [
        { value: 'compact', label: 'Compact - Icon bar only' },
        { value: 'cards', label: 'Cards - Resource links as cards' },
        { value: 'hybrid', label: 'Hybrid - Social icons + resource cards' }
      ]
    },
    {
      name: 'socialLinks',
      type: 'text',
      label: 'Social Media Links',
      helpText: 'Add links to your social media profiles'
    },
    {
      name: 'resourceLinks',
      type: 'text',
      label: 'Resource Links',
      helpText: 'Add important links like ticket booking, downloads, etc.'
    },
    {
      name: 'showContactCta',
      type: 'toggle',
      label: 'Show Contact CTA Section',
      helpText: 'Display a call-to-action section at the bottom'
    },
    {
      name: 'contactCtaHeading',
      type: 'text',
      label: 'Contact CTA Heading'
    },
    {
      name: 'contactCtaDescription',
      type: 'textarea',
      label: 'Contact CTA Description'
    },
    {
      name: 'contactCtaButtonText',
      type: 'text',
      label: 'Contact CTA Button Text'
    },
    {
      name: 'contactCtaButtonLink',
      type: 'text',
      label: 'Contact CTA Button Link'
    }
  ]
},

{
  type: 'case_studies',
  label: 'Case Studies',
  icon: 'WorkIcon',
  description: 'Past partnership success stories',
  category: 'impact',
  defaultData: {
    cases: [],
    layout: 'detailed'
  },
  editFields: [
    {
      name: 'cases',
      type: 'text',  // ✅ Use 'text' as placeholder type
      label: 'Case Studies',
      required: true,
      helpText: 'Add partnership success stories'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'detailed', label: 'Detailed' },
        { value: 'summary', label: 'Summary' }
      ]
    }
  ]
},

  // ============================================================================
  // PROGRAMS BLOCKS
  // ============================================================================
 {
  type: 'offerings',
  label: 'Offerings',
  icon: 'CardGiftcardIcon',
  description: 'Partnership packages and sponsorship opportunities',
  category: 'programs',
  defaultData: {
    title: '',
    subtitle: '',
    offerings: [],
    layout: 'cards'
  },
  editFields: [
    {
      name: 'title',
      type: 'text',
      label: 'Section Title',
      required: false,
      helpText: 'e.g., "Partnership Opportunities" or "What We Can Offer"'
    },
    {
      name: 'subtitle',
      type: 'textarea',
      label: 'Section Subtitle',
      required: false,
      helpText: 'Brief description tailored to your target sponsor'
    },
    {
      name: 'offerings',
      type: 'textarea',
      label: 'Offerings',
      required: true,
      helpText: 'Add partnership packages and opportunities'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'cards', label: 'Cards' },
        { value: 'list', label: 'List' },
        { value: 'comparison', label: 'Comparison' }
      ]
    }
  ]
},


  // ============================================================================
  // MEDIA BLOCKS
  // ============================================================================
{
  type: 'gallery',
  label: 'Gallery',
  icon: 'PhotoLibraryIcon',
  description: 'Image gallery with multiple layout options',
  category: 'media',
  defaultData: {
    images: [],  // Changed from mediaIds to images
    layout: 'grid',
    columns: 3,
    showCaptions: false
  },
  editFields: [
    {
      name: 'images',  // Changed from mediaIds
      type: 'text',    // Placeholder type (handled as array)
      label: 'Gallery Images',
      required: true,
      helpText: 'Add images to your gallery'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'grid', label: 'Grid' },
        { value: 'masonry', label: 'Masonry' },
        { value: 'carousel', label: 'Carousel' }
      ]
    },
  {
  name: 'columns',
  type: 'select',
  label: 'Columns',
  required: true,
  options: [
    { value: '2', label: '2 Columns' },  // ← Changed to strings
    { value: '3', label: '3 Columns' },
    { value: '4', label: '4 Columns' }
  ]
},
    {
      name: 'showCaptions',
      type: 'toggle',
      label: 'Show Captions',
      required: false,
      helpText: 'Display captions below images'
    }
  ]
},

{
  type: 'testimonials',
  label: 'Testimonials',
  icon: 'FormatQuoteIcon',
  description: 'Quotes from artists, partners, or attendees',
  category: 'media',
  defaultData: {
    title: '',
    subtitle: '',
    categories: [],
    testimonialIds: [],
    testimonials: [],
    layout: 'grid',
    showCategories: false
  },
  editFields: [
    {
      name: 'title',
      type: 'text',
      label: 'Section Title',
      required: false,
      helpText: 'e.g., "What Our Partners Say"'
    },
    {
      name: 'subtitle',
      type: 'textarea',
      label: 'Section Subtitle',
      required: false,
      helpText: 'Brief description that appears below the title'
    },
    {
      name: 'testimonials',
      type: 'textarea',
      label: 'Testimonials',
      required: true,
      helpText: 'Enter testimonials'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'grid', label: 'Grid' },
        { value: 'carousel', label: 'Carousel' },
        { value: 'list', label: 'List' }
      ]
    }
  ]
},

  // ============================================================================
  // ENGAGEMENT BLOCKS
  // ============================================================================
{
  type: 'rich_text',
  label: 'Rich Text',
  icon: 'TextFieldsIcon',
  description: 'Flexible rich text content with formatting',
  category: 'foundation',
  defaultData: {
    content: '<h2>Your Heading Here</h2><p>Your content here...</p>',
    alignment: 'left',
    backgroundColor: '',
    backgroundImage: '',
    overlayOpacity: 0.5,   // ADD THIS
    fullWidth: false,
    maxWidth: 'standard'  // ✅ ADD THIS - defaults to 1200px
  },
  editFields: [
    {
      name: 'content',
      type: 'rich-text',
      label: 'Content',
      required: true
    },
    {
      name: 'alignment',
      type: 'select',
      label: 'Text Alignment',
      options: ['left', 'center', 'right'],
      helpText: 'Align text within the block'
    },
    {
      name: 'maxWidth',
      type: 'select',
      label: 'Content Width',
      options: [
        { value: 'narrow', label: 'Narrow (900px)' },
        { value: 'standard', label: 'Standard (1200px)' },
        { value: 'wide', label: 'Wide (1400px)' },
        { value: 'full', label: 'Full Width' }
      ],
      helpText: 'Maximum width of the text content'
    },
      {
      name: 'backgroundImage',
      type: 'image-picker',
    label: 'Background Image',
    helpText: 'Optional background image for the block'
    },
 {
  name: 'overlayOpacity',
  type: 'slider',
  label: 'Overlay Strength',
  min: -1,
  max: 1,
  step: 0.1,
  helpText: 'Negative = lighten, Positive = darken'
},
    {
      name: 'backgroundColor',
      type: 'color',
      label: 'Background Color',
      helpText: 'Optional background color for the block'
    },
    {
      name: 'fullWidth',
      type: 'toggle',
      label: 'Full Width Block',
      helpText: 'Extend block edge-to-edge (overrides max width)'
    }
  ]
},
{
  type: 'artist',
  label: 'Artists',
  icon: 'PersonSearchIcon',
  description: 'Showcase selected artists from your database',
  category: 'programs',
  defaultData: {
    heading: 'Artists',
    subheading: '',
    artistIds: [],
    backgroundImage: '', 
    layout: 'grid'
  },
  editFields: [
    {
      name: 'heading',
      type: 'text',
      label: 'Section Heading',
      required: true,
      helpText: 'Main title for this section'
    },
    {
      name: 'subheading',
      type: 'textarea',
      label: 'Subheading',
      required: false,
      helpText: 'Optional introductory text below the heading'
    },
    {
      name: 'artistIds',
      type: 'artistSelector',
      label: 'Select Artists',
      required: true,
      helpText: 'Search and select up to 5 artists from your database',
    },
    {
      name: 'backgroundImage',
      type: 'image-picker',
    label: 'Background Image',
    helpText: 'Optional background image for the block'
    },
    {
      name: 'layout',
      type: 'select',
      label: 'Layout',
      required: true,
      options: [
        { value: 'grid', label: 'Grid' },
        { value: 'list', label: 'List' }
      ]
    }
  ]
},
  {
    type: 'contact_form',
    label: 'Contact Form',
    icon: 'EmailIcon',
    description: 'Lead capture form',
    category: 'engagement',
    defaultData: {
      fields: [
        { name: 'name', type: 'text', label: 'Name', required: true },
        { name: 'email', type: 'email', label: 'Email', required: true },
        { name: 'message', type: 'textarea', label: 'Message', required: true }
      ],
      submitText: 'Send Message',
      webhookUrl: ''
    },
    editFields: [
      {
        name: 'fields',
        type: 'textarea',
        label: 'Form Fields',
        required: true,
        helpText: 'Define form fields'
      },
      {
        name: 'submitText',
        type: 'text',
        label: 'Submit Button Text',
        required: true
      },
      {
        name: 'webhookUrl',
        type: 'text',
        label: 'Webhook URL',
        required: false,
        helpText: 'Where to send form submissions'
      }
    ]
  },
{
  type: 'upcoming_performances',
  label: 'Upcoming Performances',
  icon: 'event',
  description: 'Display next 3 upcoming performances with images and invitation CTA',
  category: 'engagement',
  defaultData: {
    sectionTitle: 'Upcoming Performances',
    ctaText: 'Request Invitation'
  },
  editFields: [
    {
      name: 'sectionTitle',
      type: 'text',
      label: 'Section Title',
      required: true,
      helpText: 'Headline for this section'
    },
    {
      name: 'ctaText',
      type: 'text',
      label: 'Button Text',
      required: true,
      helpText: 'Text for the invitation request button'
    }
  ],
  suitableFor: ['corporate_sponsorship']
},
{
  type: 'past_impact',
  label: 'Past Impact',
  icon: 'timeline',
  description: 'Showcase achievements from a specific year with metrics, featured artists, and satisfaction rates',
  category: 'impact',
  defaultData: {
    year: new Date().getFullYear().toString(),
    metrics: [
      { label: 'Performances Delivered', value: 0 },
      { label: 'Audience Members', value: 0 },
      { label: 'Total Economic Impact', value: 0 },
      { label: 'Sold-Out Shows', value: 0 }
    ],
    featuredArtists: [],
    satisfactionRate: {
      percentage: 0,
      source: ''
    }
  },
  editFields: [
    {
      name: 'year',
      type: 'text',
      label: 'Year',
      required: true,
      helpText: 'Which year does this data represent?'
    },
    {
      name: 'metrics',
      type: 'multi-select',
      label: 'Key Metrics',
      required: false,
      helpText: 'Add label and value pairs (e.g., "1600 Hours of Rehearsal Time")'
    },
    {
      name: 'featuredArtists',
      type: 'multi-select',
      label: 'Featured Artists',
      required: false,
      helpText: 'List artists with country in brackets: "Artist Name (Country)"'
    },
    {
      name: 'satisfactionRate.percentage',
      type: 'number',
      label: 'Satisfaction Rate (%)',
      required: false,
      min: 0,
      max: 100,
      helpText: 'Audience satisfaction percentage'
    },
    {
      name: 'satisfactionRate.source',
      type: 'text',
      label: 'Satisfaction Source',
      required: false,
      helpText: 'Where this data comes from (e.g., "Post-Event Survey, March 2024")'
    }
  ],
  suitableFor: ['corporate_sponsorship', 'arts_council']
},
   {
    type: 'team',
    label: 'Team',
    icon: 'GroupIcon',
    description: 'Key personnel and advisors',
    category: 'engagement',
    defaultData: {
      members: [],
      advisors: [],
      layout: 'grid'
    },
    editFields: [
      {
        name: 'members',
        type: 'textarea',
        label: 'Team Members',
        required: true,
        helpText: 'Managed as array - use form builder'
      },
      {
        name: 'advisors',
        type: 'textarea',
        label: 'Advisors',
        required: false,
        helpText: 'Managed as array - use form builder'
      },
      {
        name: 'layout',
        type: 'select',
        label: 'Layout',
        required: true,
        options: [
          { value: 'grid', label: 'Grid' },
          { value: 'list', label: 'List' }
        ]
      }
    ]
  }
];

// Helper function to get block config by type
export const getBlockByType = (type: BlockType): BlockRegistryEntry | undefined => {
  return BLOCK_REGISTRY.find(block => block.type === type);
};

// Helper function to get blocks by category
export const getBlocksByCategory = (category: string): BlockRegistryEntry[] => {
  return BLOCK_REGISTRY.filter(block => block.category === category);
};

// Get all unique categories
export const getCategories = (): string[] => {
  return Array.from(new Set(BLOCK_REGISTRY.map(block => block.category)));
};